<template>
  <Modal
    size="md"
    @close="$emit('close')"
  >
    <template #header>
      {{ $t('HeadContract.CenSchedule.AddCenModal.title') }}
    </template>

    <template #body>
      <form
        class="flex flex-col space-y-4 max-h-[60vh] overflow-y-auto pr-1"
        @submit.prevent="submit"
      >
        <label class="block">
          <span class="text-sm font-medium text-gray-700">{{ $t('HeadContract.CenSchedule.AddCenModal.label.name') }} <span class="text-red-500">*</span></span>

          <AppInput
            v-model="form.form.name"
            class="form-input mt-1"
            type="text"
            name="name"
            :placeholder="$t('HeadContract.CenSchedule.AddCenModal.placeholder.name')"
            :error="form.getErrors('name')"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">{{ $t('HeadContract.CenSchedule.AddCenModal.label.cause') }} <span class="text-red-500">*</span></span>

          <textarea
            v-model="form.form.cause"
            name="cause"
            rows="3"
            class="form-input resize-none mt-1"
            :placeholder="$t('HeadContract.CenSchedule.AddCenModal.placeholder.cause')"
          />

          <p
            v-if="form.getErrors('cause')"
            class="text-sm text-red-600 mt-1"
          >
            {{ form.getErrors('cause') }}
          </p>
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">{{ $t('HeadContract.CenSchedule.AddCenModal.label.effect') }} <span class="text-red-500">*</span></span>

          <textarea
            v-model="form.form.effect"
            name="effect"
            rows="3"
            class="form-input resize-none mt-1"
            :placeholder="$t('HeadContract.CenSchedule.AddCenModal.placeholder.effect')"
          />

          <p
            v-if="form.getErrors('effect')"
            class="text-sm text-red-600 mt-1"
          >
            {{ form.getErrors('effect') }}
          </p>
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">{{ $t('HeadContract.CenSchedule.AddCenModal.label.impact') }} <span class="text-red-500">*</span></span>

          <select
            v-model.number="form.form.impact"
            name="impact"
            class="form-input mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-ccm-blue-500 focus:ring-ccm-blue-500"
            :class="{ 'border-red-500': form.getErrors('impact') }"
          >
            <option :value="ContractEventImpact.low">
              {{ $t('HeadContract.CenSchedule.AddCenModal.impact.low') }}
            </option>

            <option :value="ContractEventImpact.medium">
              {{ $t('HeadContract.CenSchedule.AddCenModal.impact.medium') }}
            </option>

            <option :value="ContractEventImpact.high">
              {{ $t('HeadContract.CenSchedule.AddCenModal.impact.high') }}
            </option>
          </select>

          <p
            v-if="form.getErrors('impact')"
            class="text-sm text-red-600 mt-1"
          >
            {{ form.getErrors('impact') }}
          </p>
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">{{ $t('HeadContract.CenSchedule.AddCenModal.label.dateOfOccurrence') }}</span>

          <AppInput
            v-model="form.form.date_of_occurrence"
            class="form-input mt-1"
            type="date"
            name="date_of_occurrence"
            :error="form.getErrors('date_of_occurrence')"
          />
        </label>

        <div>
          <span class="text-sm font-medium text-gray-700">{{ $t('HeadContract.CenSchedule.AddCenModal.label.attachments') }}</span>

          <FileUpload
            v-model="form.form.images"
            class="mt-1"
            multiple
            @change="addFile"
          />

          <div
            v-if="filesUploaded.length"
            class="mt-3 flex flex-wrap gap-2"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="form.getErrors(`images.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </div>
      </form>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.CenSchedule.AddCenModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="createAction.is(States.LOADING)"
          @click="submit"
        >
          {{ createAction.is(States.LOADING) ? $t('HeadContract.CenSchedule.AddCenModal.button.saving') : $t('HeadContract.CenSchedule.AddCenModal.button.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput } from '@/helpers';
import { t } from '@/i18n';
import { ContractEventImpact } from '@/models/ContractEvent';
import { useContractEventsStore } from '@/store/contractEvents';
import Swal from 'sweetalert2';
import { onMounted } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'created'): void;
}>();

const contractEventsStore = useContractEventsStore();
const createAction = useStoreApiAction(contractEventsStore.createContractEvent);

const initialForm = () => ({
  name: '',
  cause: '',
  effect: '',
  impact: ContractEventImpact.low,
  date_of_occurrence: dateForDateInput(new Date()) ?? '',
  images: [] as File[],
});

const form = useFormData(initialForm());

onMounted(() => {
  form.resetData();
  form.resetErrors();
});

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (_file, index) => {
  form.form.images.splice(index, 1);
});

const submit = () => {
  createAction.request(props.projectId, form.toFormDataObject()).then(() => {
    Swal.fire({
      title: t('HeadContract.CenSchedule.AddCenModal.successTitle'),
      icon: 'success',
    }).then(() => {
      emit('created');
      emit('close');
    });
  }).catch((error) => {
    form.setErrors(error.data);
  });
};
</script>
